import { Box, Button, IconButton, TextField, Typography } from "@mui/material";
import { StyledCreateInvoiceItemTable } from "./createInvoiceItemTable.styled";
import { useContext, useEffect, useState } from "react";
import { DocumentContext } from "../../../globals/LanguageContext";
import {
  Check,
  CheckIcon,
  PencilSimpleIcon,
  Plus,
  PlusIcon,
  TrashSimpleIcon,
  X,
} from "@phosphor-icons/react";
import { Project } from "../../../models/Project";
import {
  InvoiceCreateDto,
  InvoiceItemDto,
} from "../../../models/dto/CreateInvoiceDto";
import { useTextFormatter } from "../../../hooks/useTextFormatter";
import { CreateInvoiceItemRow } from "./createInvoiceItemRow";
import { AddInvoiceItem } from "./addInvoiceItem";

type CreateInvoiceItemTableProps = {
  invoiceData: Partial<InvoiceCreateDto & { project: Project | null }>;
  onSave: (newInvoiceItem: InvoiceItemDto) => void;
  onRemove: (newInvoiceItem: InvoiceItemDto) => void;
  onEditItem: (id: string, value: string | number, index: number) => void;
};

export const CreateInvoiceItemTable = ({
  invoiceData,
  onSave,
  onRemove,
  onEditItem
}: CreateInvoiceItemTableProps) => {
  const { documentDictionary } = useContext(DocumentContext);
  const { renderText } = useTextFormatter();
  const [createNewItem, setCreateNewItem] = useState<boolean>(false);

  const [newInvoiceItem, setNewInvoiceItem] = useState<InvoiceItemDto>({
    position: 0,
    description: "",
    amount: 1,
    priceBefore: 0,
    unitprice: 0,
    unit: "C62",
    vat: invoiceData.vat!,
    priceTotal: 0,
  });

  const handleChange = (id: string, value: string | number) => {
    setNewInvoiceItem((prev) => ({ ...prev, [id]: value }));
  };

  useEffect(() => {
    setNewInvoiceItem({
      ...newInvoiceItem,
      priceBefore: Number(newInvoiceItem.unitprice!) * newInvoiceItem.amount!,
      priceTotal:
        Number(newInvoiceItem.unitprice!) *
        newInvoiceItem.amount! *
        (newInvoiceItem.vat! / 100 + 1),
    });
  }, [newInvoiceItem.unitprice, newInvoiceItem.vat, newInvoiceItem.amount]);

  const handleSave = () => {
    setCreateNewItem(false);
    onSave(newInvoiceItem);
    setNewInvoiceItem({
      position: 0,
      description: "",
      amount: 1,
      priceBefore: 0,
      unitprice: 0,
      unit: "C62",
      vat: invoiceData.vat!,
      priceTotal: 0,
    });
  };

  return (
    <StyledCreateInvoiceItemTable>
      <Box className="header">
        <Typography variant="h4" className="position tableItem">
          {documentDictionary.invoice.invoiceItems.position}
        </Typography>
        <Typography variant="h4" className="description tableItem">
          {documentDictionary.invoice.invoiceItems.description}
        </Typography>
        <Typography variant="h4" className="unitPrice tableItem">
          {documentDictionary.invoice.invoiceItems.unitPrice}
        </Typography>
        <Typography variant="h4" className="amount tableItem">
          {documentDictionary.invoice.invoiceItems.amount}
        </Typography>
        <Typography variant="h4" className="unit tableItem">
          {documentDictionary.invoice.invoiceItems.unit}
        </Typography>
        <Typography variant="h4" className="totalBefore tableItem">
          {renderText(documentDictionary.invoice.invoiceItems.totalBefore)}
        </Typography>
        <Typography variant="h4" className="vat tableItem">
          {documentDictionary.invoice.invoiceItems.vat}
        </Typography>
        <Typography variant="h4" className="total tableItem">
          {renderText(documentDictionary.invoice.invoiceItems.total)}
        </Typography>
      </Box>

      <Box className="content">
        {invoiceData.invoiceItems?.map((invoiceItem, index) => (
          <CreateInvoiceItemRow
            index={index}
            invoiceItem={invoiceItem}
            onRemove={onRemove}
            onChange={handleChange}
            onEditItem={onEditItem}
          />
        ))}
        {createNewItem && (
          <Box className="row">
            <AddInvoiceItem
              invoiceItems={invoiceData.invoiceItems!}
              newInvoiceItem={newInvoiceItem}
              onChange={handleChange}
            />
          </Box>
        )}
        {createNewItem ? (
          <IconButton onClick={handleSave}>
            <CheckIcon size={24} />
          </IconButton>
        ) : (
          <IconButton onClick={() => setCreateNewItem(true)}>
            <PlusIcon size={24} />
          </IconButton>
        )}
      </Box>
    </StyledCreateInvoiceItemTable>
  );
};
